import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Modal } from "../../components/modal/Modal";
import { Box, styled, TextField } from "@mui/material";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getAllEquipment, postRoomEquipment } from "redux/actions/equipmentActions";

type Props = {
  isVisible: boolean;
  setIsVisible: (value: boolean) => void;
  roomId: number;
};

const EquipmentContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  marginBottom: "16px",
});

const IndividualFieldsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

export const RoomInventoryModal = ({ isVisible, setIsVisible, roomId }: Props) => {
  const dispatch = useAppDispatch();
  const { allEquipment, allEquipmentLoadingState } = useAppSelector((state) => state.equipment);

  const [draftEquipmentNumbers, setDraftEquipmentNumbers] = useState<string[]>([]);
  const [csvText, setCsvText] = useState<string>("");

  // Update CSV text when equipment array changes
  useEffect(() => {
    const newCsvText = draftEquipmentNumbers.join(", ");
    setCsvText(newCsvText);
  }, [draftEquipmentNumbers]);

  useEffect(() => {
    if (isVisible) {
      dispatch(getAllEquipment());
    }
  }, [dispatch, isVisible]);

  const existingEquipmentForThisRoom = useMemo(
    () =>
      allEquipment
        .filter((eq) => eq.equipmentType === "ROOM" && eq.room?.id === roomId)
        .map((eq) => eq.inventoryNumber!),
    [allEquipment, roomId],
  );

  useEffect(() => {
    setDraftEquipmentNumbers(existingEquipmentForThisRoom);
  }, [existingEquipmentForThisRoom]);

  // Handle CSV text field changes (just update the text)
  const handleCsvChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCsvText(event.target.value);
  };

  // Parse CSV when button is clicked
  const handleParseCsv = () => {
    const items = csvText
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    setDraftEquipmentNumbers(items);
  };

  // Handle individual field changes
  const handleEquipmentChange = (index: number, value: string) => {
    const newEquipment = [...draftEquipmentNumbers];
    newEquipment[index] = value;
    setDraftEquipmentNumbers(newEquipment);
  };

  // Add new equipment field
  const handleAddEquipment = () => {
    setDraftEquipmentNumbers([...draftEquipmentNumbers, ""]);
  };

  // Remove equipment field
  const handleRemoveEquipment = (index: number) => {
    const newEquipment = draftEquipmentNumbers.filter((_, i) => i !== index);
    setDraftEquipmentNumbers(newEquipment);
  };

  const equipmentThatWillBeOverriden = useMemo(() => {
    const numbersFromOtherLocations = allEquipment
      .map((eq) => eq.inventoryNumber!)
      .filter((num) => !existingEquipmentForThisRoom.includes(num));
    return numbersFromOtherLocations.filter((num) =>
      draftEquipmentNumbers.map((den) => den.trim().toUpperCase()).includes(num),
    );
  }, [allEquipment, draftEquipmentNumbers, existingEquipmentForThisRoom]);

  const handleSubmit = async () => {
    await dispatch(
      postRoomEquipment({
        roomId,
        inventoryNumbers: draftEquipmentNumbers,
      }),
    ).unwrap();

    dispatch(getAllEquipment());
    setIsVisible(false);
  };

  if (allEquipmentLoadingState === "NotLoaded" || allEquipmentLoadingState === "Loading") {
    return (
      <Modal show={isVisible} handleClose={() => setIsVisible(false)}>
        <Box>Loading...</Box>
      </Modal>
    );
  }

  return (
    <Modal show={isVisible} handleClose={() => setIsVisible(false)}>
      <EquipmentContainer>
        <Box style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
          <TextField
            label="Equipment (CSV)"
            placeholder="Enter equipment separated by commas (e.g., Laptop, Mouse, Keyboard)"
            value={csvText}
            onChange={handleCsvChange}
            multiline
            rows={3}
            fullWidth
          />
          <button
            type="button"
            onClick={handleParseCsv}
            className="btn btn--green"
            style={{
              padding: "8px 16px",
              marginTop: "8px",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            Apply CSV
          </button>
        </Box>

        <Box>
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <strong>Individual Equipment:</strong>
            <button
              type="button"
              onClick={handleAddEquipment}
              className="btn btn--green"
              style={{
                padding: "4px 12px",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              + Add
            </button>
          </Box>

          <IndividualFieldsContainer>
            {draftEquipmentNumbers.length === 0 ? (
              <Box style={{ color: "#666", fontStyle: "italic", padding: "8px" }}>
                No equipment added. Type in the CSV field above or click "Add" to add individual items.
              </Box>
            ) : (
              draftEquipmentNumbers.map((item, index) => (
                <Box key={index} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <TextField
                    value={item}
                    onChange={(e) => handleEquipmentChange(index, e.target.value)}
                    placeholder={`Equipment ${index + 1}`}
                    size="small"
                    fullWidth
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveEquipment(index)}
                    className="btn"
                    style={{
                      padding: "4px 12px",
                      fontSize: "12px",
                      backgroundColor: "#dc3545",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}>
                    Remove
                  </button>
                </Box>
              ))
            )}
          </IndividualFieldsContainer>
        </Box>
      </EquipmentContainer>
      {equipmentThatWillBeOverriden.length > 0 && (
        <div style={{ marginBottom: "16px", color: "red", fontSize: "14px" }}>
          <p>Equipment will be removed from its current location and assigned to this room:</p>
          <ul>
            {equipmentThatWillBeOverriden.map((eq) => (
              <li key={eq}>- {eq}</li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        className="form__btn btn btn--green"
        style={{
          width: "100%",
          margin: "5px 0 5px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
        SUBMIT
      </button>
    </Modal>
  );
};
